import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Nav, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import QuestionsList from '../../../Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL

// Enhanced CircularProgress component with better styling
const CircularProgress = ({ percentage, size = 80, showLabel = true }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Get color based on percentage
  const getProgressColor = (percent) => {
    if (percent >= 100) return '#198754'; // success/green
    if (percent >= 75) return '#0d6efd';  // primary/blue
    if (percent >= 50) return '#ffc107';  // warning/yellow
    if (percent >= 25) return '#fd7e14';  // orange
    return '#dc3545';                      // danger/red
  };

  const progressColor = getProgressColor(percentage);

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-bg"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-bar"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showLabel && (
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        )}
      </svg>
    </div>
  );
};

function getStatusBadgeClass(status) {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'badge bg-danger'; // Red for pending
    case 'in progress':
      return 'badge bg-warning text-dark'; // Yellow for in progress
    case 'completed':
      return 'badge bg-success'; // Green for completed
    default:
      return 'badge bg-secondary';
  }
}

function InDuserAudit() {
  const [checklists, setChecklists] = useState({
    pending: [],
    inProgress: [],
    completed: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklistContent, setChecklistContent] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [subsectionQuestions, setSubsectionQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedSections, setExpandedSections] = useState({});
  const [answers, setAnswers] = useState({});
  const [checklistProgress, setChecklistProgress] = useState({});
  const [subsectionProgress, setSubsectionProgress] = useState({});
  const [currentView, setCurrentView] = useState('overview'); // 'overview', 'subsectionQuestions'
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('dUser'));
  const BranchCode = userData?.BranchCode;
  const department = userData?.department;

  // Get tab from URL if present
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');

  useEffect(() => {
    // Set active tab based on URL parameter if present
    if (tabParam) {
      if (tabParam === 'all') {
        setActiveTab('pending'); // Default to pending when "all" is selected
      } else if (['pending', 'inProgress', 'completed'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    }
  }, [tabParam]);

  useEffect(() => {
    if (BranchCode && department) {
      fetchDepartmentChecklists();
    }
  }, [BranchCode, department]);

  const fetchDepartmentChecklists = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/get-department-assigned-checklists/${BranchCode}/${department}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const checklistsData = data.checklists;
        
        // Categorize checklists by status
        const categorized = {
          pending: checklistsData.filter(cl => cl.status === 'Pending'),
          inProgress: checklistsData.filter(cl => cl.status === 'In progress'),
          completed: checklistsData.filter(cl => cl.status === 'Completed')
        };

        setChecklists(categorized);
        
        // Fetch progress for each checklist
        await fetchAllChecklistProgress(checklistsData);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
      alert('Failed to fetch checklists');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChecklistProgress = async (checklistsData) => {
    try {
      const progressPromises = checklistsData.map(async (checklist) => {
        try {
          const response = await fetch(
            `${BASE_URL}/get-department-checklist-progress/${checklist.checklist_id}/${BranchCode}/${department}`
          );

          if (response.ok) {
            const data = await response.json();
            return {
              id: checklist.id,
              progress: data.progress || {
                progressPercentage: 0,
                answeredQuestions: 0,
                totalQuestions: 0
              }
            };
          }
        } catch (error) {
          console.error(`Error fetching progress for checklist ${checklist.id}:`, error);
        }
        
        return {
          id: checklist.id,
          progress: {
            progressPercentage: 0,
            answeredQuestions: 0,
            totalQuestions: 0
          }
        };
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(result => {
        progressMap[result.id] = result.progress;
      });
      
      setChecklistProgress(progressMap);
    } catch (error) {
      console.error('Error fetching checklist progress:', error);
    }
  };

  const fetchSubsectionProgress = async (checklistId, subsectionPath = '') => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-department-subsection-progress/${checklistId}/${BranchCode}/${department}/${subsectionPath}`
      );

      if (response.ok) {
        const data = await response.json();
        return data.progress || {
          progressPercentage: 0,
          answeredQuestions: 0,
          totalQuestions: 0
        };
      }
    } catch (error) {
      console.error('Error fetching subsection progress:', error);
    }
    
    return {
      progressPercentage: 0,
      answeredQuestions: 0,
      totalQuestions: 0
    };
  };

  const fetchChecklistContent = async (checklistId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-department-checklist-content/${checklistId}/${BranchCode}/${department}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setChecklistContent(data.content);
        
        // Fetch questions for this department with responses
        const questionsResponse = await fetch(
          `${BASE_URL}/get-department-questions/${checklistId}/${BranchCode}/${department}`
        );

        if (!questionsResponse.ok) {
          throw new Error(`HTTP error! Status: ${questionsResponse.status}`);
        }

        const questionsData = await questionsResponse.json();

        if (questionsData.success) {
          setQuestions(questionsData.questions);
          
          // Initialize answers from existing responses
          const initialAnswers = {};
          questionsData.questions.forEach(question => {
            if (question.response) {
              initialAnswers[question.id] = {
                answer: question.response.answer,
                points: question.response.points,
                remarks: question.response.remarks,
                cap: question.response.cap,
                mediaFiles: question.response.media_file ? [{
                  filename: question.response.media_file,
                  isNew: false
                }] : []
              };
            }
          });
          setAnswers(initialAnswers);
        }

        // Fetch progress for all subsections
        if (data.content.subsections && data.content.subsections.length > 0) {
          await fetchAllSubsectionProgress(checklistId, data.content.subsections);
        }
      }
    } catch (error) {
      console.error('Error fetching checklist content:', error);
      alert('Failed to fetch checklist content');
    }
  };

  const fetchAllSubsectionProgress = async (checklistId, subsections, parentPath = '') => {
    const progressPromises = [];
    
    const processSubsections = (subs, path) => {
      subs.forEach(subsection => {
        const subsectionPath = path ? `${path}|${subsection.name}` : subsection.name;
        
        // Add progress fetch for this subsection
        progressPromises.push(
          fetchSubsectionProgress(checklistId, subsectionPath).then(progress => ({
            path: subsectionPath,
            progress
          }))
        );
        
        // Recursively process nested subsections
        if (subsection.subsections && subsection.subsections.length > 0) {
          processSubsections(subsection.subsections, subsectionPath);
        }
      });
    };

    processSubsections(subsections, parentPath);
    
    try {
      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(result => {
        progressMap[result.path] = result.progress;
      });
      setSubsectionProgress(progressMap);
    } catch (error) {
      console.error('Error fetching all subsection progress:', error);
    }
  };

  const fetchSubsectionQuestions = async (checklistId, subsectionPath) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-department-subsection-questions/${checklistId}/${BranchCode}/${department}/${subsectionPath}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSubsectionQuestions(data.questions);
        setSelectedSubsection(data.subsection);
        
        // Initialize answers for subsection questions
        const subsectionAnswers = {};
        data.questions.forEach(question => {
          if (question.response) {
            subsectionAnswers[question.id] = {
              answer: question.response.answer,
              points: question.response.points,
              remarks: question.response.remarks,
              cap: question.response.cap,
              mediaFiles: question.response.media_file ? [{
                filename: question.response.media_file,
                isNew: false
              }] : []
            };
          }
        });
        setAnswers(prev => ({ ...prev, ...subsectionAnswers }));
      }
    } catch (error) {
      console.error('Error fetching subsection questions:', error);
      alert('Failed to fetch subsection questions');
    }
  };

  const handleChecklistClick = (checklist) => {
    setSelectedChecklist(checklist);
    setChecklistContent(null);
    setQuestions([]);
    setSelectedSubsection(null);
    setSubsectionQuestions([]);
    setCurrentView('overview');
    setBreadcrumbPath([]);
    fetchChecklistContent(checklist.checklist_id);
  };

  const handleSubsectionClick = (subsection, path = '') => {
    const subsectionPath = path ? `${path}|${subsection.name}` : subsection.name;
    
    // Create breadcrumb path
    const pathParts = subsectionPath.split('|');
    setBreadcrumbPath(pathParts);
    
    fetchSubsectionQuestions(selectedChecklist.checklist_id, subsectionPath);
    setCurrentView('subsectionQuestions');
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const backToOverview = () => {
    setCurrentView('overview');
    setSelectedSubsection(null);
    setSubsectionQuestions([]);
    setBreadcrumbPath([]);
  };

  const renderBreadcrumb = () => {
    if (breadcrumbPath.length === 0) return null;

    return (
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <button 
              className="btn btn-link p-0 text-decoration-none"
              onClick={backToOverview}
            >
              <i className="bi bi-house me-1"></i>
              {checklistContent?.name}
            </button>
          </li>
          {breadcrumbPath.map((part, index) => (
            <li 
              key={index} 
              className={`breadcrumb-item ${index === breadcrumbPath.length - 1 ? 'active' : ''}`}
              aria-current={index === breadcrumbPath.length - 1 ? 'page' : undefined}
            >
              {index === breadcrumbPath.length - 1 ? part : (
                <button className="btn btn-link p-0 text-decoration-none">
                  {part}
                </button>
              )}
            </li>
          ))}
        </ol>
      </nav>
    );
  };

  const renderSubsections = (subsections, path = '') => {
    return (
      <Row xs={1} md={2} lg={4} className="g-4">
        {subsections.map((subsection, index) => {
          const sectionId = `${path}-${index}`;
          const subsectionPath = path ? `${path}|${subsection.name}` : subsection.name;
          const hasQuestions = subsection.questions && subsection.questions.length > 0;
          const hasSubsections = subsection.subsections && subsection.subsections.length > 0;
          
          // Get progress for this subsection
          const progress = subsectionProgress[subsectionPath] || {
            progressPercentage: 0,
            answeredQuestions: 0,
            totalQuestions: subsection.questions ? subsection.questions.length : 0
          };

          return (
            <Col key={sectionId}>
              <Card 
                className="h-100 shadow-sm hover-shadow section-card"
                onClick={hasQuestions ? () => handleSubsectionClick(subsection, path) : undefined}
                role={hasQuestions ? "button" : undefined}
                style={{ cursor: hasQuestions ? 'pointer' : 'default' }}
              >
                <Card.Body className="d-flex flex-column">
                  {/* Progress Circle */}
                  {hasQuestions && (
                    <div className="text-center mb-3">
                      <CircularProgress 
                        percentage={progress.progressPercentage}
                        size={80}
                        showLabel={true}
                      />
                    </div>
                  )}
                  
                  {/* Subsection Header */}
                  <div className="text-center mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="card-title text-primary fw-bold mb-0 flex-grow-1">{subsection.name}</h5>
                      <div className="d-flex gap-1 flex-wrap">
                        
                        {hasSubsections && 
                          <Badge bg="info" pill>
                            <i className="bi bi-folder me-1"></i>
                            {subsection.subsections.length}
                          </Badge>
                        }
                      </div>
                    </div>
                    {hasQuestions && (
                      <small className="text-muted d-block">
                        {progress.totalQuestions} Total Questions
                      </small>
                    )}
                  </div>

                  {/* Progress Details */}
                  {hasQuestions && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-center gap-3 mb-2">
                        <div className="text-success">
                          <small className="fw-semibold">{progress.answeredQuestions} Answered</small>
                        </div>
                        <div className="text-danger">
                          <small className="fw-semibold">{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <small className={`badge ${
                          progress.progressPercentage === 100 ? 'bg-success' :
                          progress.progressPercentage > 0 ? 'bg-warning text-dark' : 'bg-danger'
                        }`}>
                          {progress.progressPercentage === 100 ? 'Completed' :
                          progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                        </small>
                      </div>
                    </div>
                  )}
                  
                 
                </Card.Body>
                
                {/* Render nested subsections if expanded */}
                {hasSubsections && expandedSections[sectionId] && (
                  <div className="card-footer bg-light">
                    <div className="nested-subsections">
                      {renderSubsections(subsection.subsections, subsectionPath)}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderChecklistGrid = (checklistArray, status) => {
    if (checklistArray.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="empty-state">
            <i className="bi bi-clipboard-x display-1 text-muted mb-3"></i>
            <h5 className="text-muted mb-2">No {status.toLowerCase()} checklists found</h5>
            <p className="text-muted">There are no {status.toLowerCase()} checklists for your department at the moment.</p>
          </div>
        </div>
      );
    }

    return (
      <Row xs={1} md={2} lg={4} className="g-4">
        {checklistArray.map((checklist) => {
          const progress = checklistProgress[checklist.id] || {
            progressPercentage: 0,
            answeredQuestions: 0,
            totalQuestions: 0
          };

          return (
            <Col key={checklist.id}>
              <Card 
                className={`h-100 shadow-sm hover-shadow checklist-card ${status.toLowerCase() === 'completed' ? 'border-success' : ''}`}
                onClick={() => handleChecklistClick(checklist)}
                role="button"
              >
                <Card.Body className="d-flex flex-column p-3">
                  {/* Progress Circle */}
                  <div className="text-center mb-2">
                    <CircularProgress 
                      percentage={progress.progressPercentage}
                      size={70}
                      showLabel={true}
                    />
                  </div>
                  
                  {/* Title and Status */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title text-primary fw-bold mb-0 flex-grow-1">{checklist.checklist_name}</h6>
                    <Badge className={getStatusBadgeClass(status)} pill>
                      {status}
                    </Badge>
                  </div>
          
                  {/* Questions Count and Date */}
                  <div className="mb-2">
                    <small className="text-muted text-center d-block mb-1">
                      {progress.totalQuestions} Total Questions
                    </small>
                    <small className="text-muted text-center d-block">
                      <i className="bi bi-calendar me-1"></i>
                      Started: {new Date(checklist.start_date).toLocaleDateString()}
                    </small>
                  </div>
          
                  {/* Progress Stats */}
                  <div className="mt-auto">
                    <div className="d-flex justify-content-center gap-3 mb-2">
                      <small className="text-success fw-semibold">
                        {progress.answeredQuestions} Answered
                      </small>
                      <small className="text-danger fw-semibold">
                        {progress.totalQuestions - progress.answeredQuestions} Remaining
                      </small>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="text-center">
                      <span className={`badge ${
                        progress.progressPercentage === 100 ? 'bg-success' :
                        progress.progressPercentage > 0 ? 'bg-warning text-dark' : 'bg-danger'
                      }`}>
                        {progress.progressPercentage === 100 ? 'Completed' :
                        progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  if (loading) {
    return (
      <Container fluid className="px-4 py-5">
        <div className="text-center py-5">
          <div className="loading-container">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Department Audits</p>
            <p className="text-muted">Please wait while we fetch your audit information...</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <div className="container-fluid py-4">
      {!selectedChecklist ? (
        /* Checklists Overview */
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="mb-4">
              <h2 className="text-primary mb-2">Audits</h2>
            </div>

            {/* Navigation Tabs */}
<div className="mb-4">
  <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="custom-nav-tabs d-none d-sm-flex">
    <Nav.Item>
      <Nav.Link eventKey="pending" className="d-flex align-items-center gap-2">
        <i className="bi bi-hourglass-split"></i>
        Plan
        <Badge bg="danger" text="light" pill>{checklists.pending.length}</Badge>
      </Nav.Link>
    </Nav.Item>
    <Nav.Item>
      <Nav.Link eventKey="inProgress" className="d-flex align-items-center gap-2">
        <i className="bi bi-arrow-clockwise"></i>
        In Progress
        <Badge bg="warning" text="dark" pill>{checklists.inProgress.length}</Badge>
      </Nav.Link>
    </Nav.Item>
    <Nav.Item>
      <Nav.Link eventKey="completed" className="d-flex align-items-center gap-2">
        <i className="bi bi-check-circle"></i>
        Completed
        <Badge bg="success" text="light" pill>{checklists.completed.length}</Badge>
      </Nav.Link>
    </Nav.Item>
  </Nav>

  {/* Mobile 2-Row Layout */}
  <div className="d-sm-none">
    <div className="row g-2">
      <div className="col-6">
        <button 
          className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'pending' ? 'btn-danger' : 'btn-outline-danger'}`}
          onClick={() => setActiveTab('pending')}
        >
          <i className="bi bi-hourglass-split"></i>
          Plan
          <Badge bg={activeTab === 'pending' ? 'light' : 'danger'} text={activeTab === 'pending' ? 'dark' : 'light'} pill>
            {checklists.pending.length}
          </Badge>
        </button>
      </div>
      <div className="col-6">
        <button 
          className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'inProgress' ? 'btn-warning' : 'btn-outline-warning'}`}
          onClick={() => setActiveTab('inProgress')}
        >
          <i className="bi bi-arrow-clockwise"></i>
          Progress
          <Badge bg={activeTab === 'inProgress' ? 'light' : 'warning'} text="dark" pill>
            {checklists.inProgress.length}
          </Badge>
        </button>
      </div>
      <div className="col-12">
        <button 
          className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'completed' ? 'btn-success' : 'btn-outline-success'}`}
          onClick={() => setActiveTab('completed')}
        >
          <i className="bi bi-check-circle"></i>
          Completed
          <Badge bg={activeTab === 'completed' ? 'light' : 'success'} text={activeTab === 'completed' ? 'dark' : 'light'} pill>
            {checklists.completed.length}
          </Badge>
        </button>
      </div>
    </div>
  </div>
</div>
            

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'pending' && renderChecklistGrid(checklists.pending, 'Pending')}
              {activeTab === 'inProgress' && renderChecklistGrid(checklists.inProgress, 'In Progress')}
              {activeTab === 'completed' && renderChecklistGrid(checklists.completed, 'Completed')}
            </div>
          </div>
        </div>
      ) : currentView === 'overview' ? (
        /* Checklist Overview */
        <div className="row">
          <div className="col-12">
            {/* Detail Header */}
              <div className="d-flex align-items-center mb-3 mb-md-0">
                <Button 
                  variant="outline-secondary"
                  className="me-3"
                  onClick={() => setSelectedChecklist(null)}
                >
                  <i className="bi bi-arrow-left me-2"></i>Back
                </Button>
               
              </div>

            {checklistContent && (
              <div className="row">
                <div className="col-12">
                  {/* Department Section */}
                  <div className="card mb-4 main-content-card">
                    
                    <div className="card-body">
                      {/* Direct Questions - Show directly without button */}
                      {checklistContent.questions && checklistContent.questions.length > 0 && (
                        <div className="mb-4">
                          <div className="card main-content-card">
                            <div className="card-header bg-primary text-white">
                              <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0">
                                  <i className="bi bi-list-check me-2"></i>
                                  Questions ({questions.filter(q => checklistContent.questions.includes(q.id)).length})
                                </h4>
                                <Badge bg="light" text="dark">
                                  <i className="bi bi-eye-fill me-1"></i>
                                  View Only
                                </Badge>
                              </div>
                            </div>
                            <div className="card-body">
                              <QuestionsList
                                questions={questions.filter(q => checklistContent.questions.includes(q.id))}
                                answers={answers}
                                handleAnswerChange={(questionId, answerData) => setAnswers(prev => ({
                                  ...prev,
                                  [questionId]: answerData
                                }))}
                                setAnswers={setAnswers}
                                selectedSection={{
                                  name: checklistContent.name,
                                  questions: checklistContent.questions
                                }}
                                isViewOnly={true}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Subsections */}
                      {checklistContent.subsections && checklistContent.subsections.length > 0 && (
                        <div>
                          <h5 className="text-secondary mb-3">
                            <i className="bi bi-folder me-2"></i>
                            Subsections
                          </h5>
                          {renderSubsections(checklistContent.subsections)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Questions View */
        <div className="row">
          <div className="col-12">
            {/* Questions Header */}
              <div className="d-flex align-items-center mb-3 mb-md-0">
                <Button 
                  variant="outline-secondary"
                  className="me-3"
                  onClick={backToOverview}
                >
                  <i className="bi bi-arrow-left me-2"></i>Back
                </Button>
                <div>
                 
              </div>
            </div>

            {/* Breadcrumb */}
            {renderBreadcrumb()}

            {/* Questions Content */}
            <div className="card main-content-card">
              <div className="card-header bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    <i className="bi bi-list-check me-2"></i>
                    Questions ({subsectionQuestions.length})
                  </h4>
                  <Badge bg="light" text="dark">
                    <i className="bi bi-eye-fill me-1"></i>
                    View Only
                  </Badge>
                </div>
              </div>
              <div className="card-body">
                {subsectionQuestions.length > 0 ? (
                  <QuestionsList
                    questions={subsectionQuestions}
                    answers={answers}
                    handleAnswerChange={(questionId, answerData) => setAnswers(prev => ({
                      ...prev,
                      [questionId]: answerData
                    }))}
                    setAnswers={setAnswers}
                    selectedSection={selectedSubsection}
                    isViewOnly={true}
                  />
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-question-circle display-4 text-muted mb-3"></i>
                    <h5 className="text-muted">No questions found</h5>
                    <p className="text-muted">This section doesn't have any questions to display.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          /* Main Layout Styles */
          .page-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            padding: 2rem;
            border-radius: 0.75rem;
            margin-bottom: 2rem;
          }

          .detail-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border: 1px solid #dee2e6;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .main-content-card {
            border: none;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border-radius: 0.75rem;
          }

          /* Enhanced Circular Progress Styles */
          .circular-progress {
            position: relative;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
          }
          
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          
          .progress-text-large {
            font-size: 18px;
            font-weight: 700;
            transition: fill 0.35s;
          }

          /* Enhanced Navigation Tabs */
          .custom-nav-tabs {
            border-bottom: 2px solid #dee2e6;
            background: #f8f9fa;
            border-radius: 0.5rem 0.5rem 0 0;
            padding: 0.5rem;
          }
          
          .custom-nav-tabs .nav-link {
            color: #6c757d;
            border: none;
            border-bottom: 2px solid transparent;
            padding: 0.75rem 1rem;
            font-weight: 500;
            transition: all 0.3s ease;
            border-radius: 0.375rem;
            margin: 0 0.25rem;
          }
          
          .custom-nav-tabs .nav-link:hover {
            border-bottom-color: #0d6efd;
            color: #0d6efd;
            background-color: rgba(13, 110, 253, 0.1);
          }
          
          .custom-nav-tabs .nav-link.active {
            color: #0d6efd;
            background-color: #fff;
            border-bottom-color: #0d6efd;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          /* Enhanced Card Hover Effects */
          .hover-shadow {
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .hover-shadow:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 25px rgba(0,0,0,0.15) !important;
          }

          .checklist-card,
          .section-card {
            border: 1px solid #dee2e6;
            border-radius: 0.75rem;
            transition: all 0.3s ease;
            background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
          }

          .checklist-card:hover,
          .section-card:hover {
            border-color: #b8c5d1;
            background: linear-gradient(145deg, #ffffff 0%, #f1f3f4 100%);
          }

          /* Enhanced Button Hover Effects */
          .btn-hover-lift {
            transition: all 0.2s ease;
          }

          .btn-hover-lift:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }

          /* Info Items */
          .info-item {
            display: flex;
            align-items: center;
            padding: 0.25rem 0;
          }

          /* Enhanced nested subsections styling */
          .nested-subsections {
            padding: 0.75rem 0;
          }

          .nested-subsections .section-card {
            margin-bottom: 0.75rem;
            border: 1px solid #dee2e6;
            transform: scale(0.98);
            background: linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%);
          }

          .nested-subsections .section-card:last-child {
            margin-bottom: 0;
          }

          .nested-subsections .section-card:hover {
            transform: scale(0.98) translateY(-2px);
            border-color: #b8c5d1;
          }

          /* Completed checklist styling */
          .checklist-card.border-success {
            border-color: #d1e7dd;
          }

          .checklist-card.border-success:hover {
            border-color: #badbcc;
          }

          /* Section Header */
          .section-header {
            background: #f8f9fa;
            padding: 1rem;
            border-radius: 0.5rem;
          }

          /* Enhanced Badge Styles */
          .badge {
            font-weight: 500;
            padding: 0.4em 0.6em;
            font-size: 0.75em;
          }

          /* Empty State */
          .empty-state {
            padding: 3rem 1rem;
            text-align: center;
          }

          /* Enhanced Loading State */
          .loading-container {
            padding: 3rem 1rem;
          }

          .spinner-grow {
            animation-duration: 1.5s;
          }

          /* Section Header */
          .section-header {
            background: #f8f9fa;
            padding: 1rem;
            border-radius: 0.5rem;
          }

          /* Enhanced Breadcrumb */
          .breadcrumb {
            background: #e9ecef;
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;
            margin-bottom: 1rem;
          }

          .breadcrumb-item a, .breadcrumb .btn-link {
            color: #0d6efd;
            text-decoration: none;
          }

          .breadcrumb-item a:hover, .breadcrumb .btn-link:hover {
            color: #0b5ed7;
            text-decoration: underline;
          }

          /* Enhanced Progress text styling */
          .fw-semibold {
            font-weight: 600;
          }

          .checklist-card.border-success {
            border-color: #d1e7dd;
          }

          .checklist-card.border-success:hover {
            border-color: #badbcc;
          }

          /* Enhanced Card body flex improvements */
          .card-body {
            position: relative;
          }

          /* Enhanced Responsive improvements */
          @media (max-width: 768px) {
            .circular-progress {
              transform: scale(0.9);
            }
            
            .card-title {
              font-size: 0.95rem;
            }

            .progress-text-large {
              font-size: 16px;
            }

            .page-header {
              padding: 1.5rem;
            }

            .detail-header {
              padding: 1rem !important;
            }
          }

          /* Enhanced Card animations */
          .section-card, .checklist-card {
            animation: fadeInUp 0.3s ease-out;
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Enhanced progress indicator */
          .circular-progress:hover {
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.15));
          }

          /* Enhanced card titles */
          .card-title {
            line-height: 1.3;
            font-size: 1rem;
          }

          /* Enhanced progress bars */
          .card:hover .circular-progress circle.progress-bar {
            filter: brightness(0.9);
          }

          /* Better responsive grid */
          @media (max-width: 576px) {
            .g-4 {
              --bs-gutter-x: 1rem;
              --bs-gutter-y: 1rem;
            }

            .card-body {
              padding: 1rem;
            }

            .btn-hover-lift {
              font-size: 0.875rem;
            }
          }

          /* Enhanced status indicators */
          .badge.bg-success {
            background: linear-gradient(45deg, #198754, #20c997) !important;
          }

          .badge.bg-warning {
            background: linear-gradient(45deg, #ffc107, #fd7e14) !important;
          }

          .badge.bg-danger {
            background: linear-gradient(45deg, #dc3545, #e63946) !important;
          }

          /* Card click feedback */
          .section-card:active,
          .checklist-card:active {
            transform: translateY(-2px) scale(0.98);
          }

          /* Better visual hierarchy */
          .text-primary.fw-bold {
            color: #0d6efd !important;
            font-weight: 600 !important;
          }

          /* Enhanced icon colors */
          .text-muted {
            color: #6c757d !important;
          }

          /* Better spacing */
          .mb-3 {
            margin-bottom: 1rem !important;
          }

          /* Card footer styling */
          .card-footer {
            background-color: #f8f9fa;
            border-top: 1px solid #dee2e6;
          }

          /* Enhanced view-only mode styling */
          .view-only .card {
            opacity: 0.95;
          }

          .view-only .btn-outline-primary i.bi-play-circle::before {
            content: "\\f341"; /* bi-eye icon */
          }

          /* Better contrast for small text */
          small {
            font-size: 0.875rem;
            line-height: 1.4;
          }

          /* Enhanced loading state */
          .loading-container .text-primary {
            color: #0d6efd !important;
          }

          /* Better focus states */
          .btn:focus,
          .card:focus {
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
          }
        `}
      </style>
    </div>
  );
}

export default InDuserAudit;